// src/utils/truckDetailsCache.js

const TRUCK_DETAILS_PREFIX =
  "truck:detail:";

const TRUCK_DETAILS_TIMESTAMP_PREFIX =
  "truck:detail:full-ts:v2:";

const TRUCK_DETAILS_TTL_MS =
  24 *
  60 *
  60 *
  1000;

const TRUCK_SUMMARY_CACHE_SUFFIX =
  ":trucks:summary:v2";

export const TRUCK_SUMMARY_CACHE_UPDATED_EVENT =
  "loadevo:truck-summary-cache-updated";

const memoryCache =
  new Map();

function isBrowser() {
  return (
    typeof window !==
    "undefined"
  );
}

function getTruckCacheKey(
  truckId
) {
  return `${TRUCK_DETAILS_PREFIX}${truckId}`;
}

function getTruckTimestampKey(
  truckId
) {
  return `${TRUCK_DETAILS_TIMESTAMP_PREFIX}${truckId}`;
}

function safeParse(
  raw
) {
  if (!raw) {
    return null;
  }

  try {
    const parsed =
      JSON.parse(
        raw
      );

    /*
     * Compatibilidade com um eventual formato:
     *
     * {
     *   savedAt,
     *   data
     * }
     */
    if (
      parsed &&
      typeof parsed ===
        "object" &&
      parsed.data &&
      typeof parsed.data ===
        "object"
    ) {
      return parsed.data;
    }

    return parsed;
  } catch {
    return null;
  }
}

function getSummaryCacheItems(
  parsed
) {
  if (
    Array.isArray(
      parsed
    )
  ) {
    return parsed;
  }

  if (
    parsed &&
    typeof parsed ===
      "object" &&
    Array.isArray(
      parsed.items
    )
  ) {
    return parsed.items;
  }

  if (
    parsed &&
    typeof parsed ===
      "object" &&
    parsed.data &&
    typeof parsed.data ===
      "object" &&
    Array.isArray(
      parsed.data.items
    )
  ) {
    return parsed.data.items;
  }

  return [];
}

function writeSummaryCacheItems(
  cacheKey,
  parsed,
  items
) {
  if (
    !isBrowser()
  ) {
    return;
  }

  const now =
    Date.now();

  let nextValue;

  if (
    Array.isArray(
      parsed
    )
  ) {
    nextValue =
      items;
  } else if (
    parsed &&
    typeof parsed ===
      "object" &&
    parsed.data &&
    typeof parsed.data ===
      "object" &&
    Array.isArray(
      parsed.data.items
    )
  ) {
    nextValue = {
      ...parsed,

      savedAt:
        now,

      updatedAt:
        now,

      meta: {
        ...(
          parsed.meta ||
          {}
        ),

        etag:
          null,
      },

      data: {
        ...parsed.data,

        items,
      },
    };
  } else {
    nextValue = {
      ...(
        parsed &&
        typeof parsed ===
          "object"
          ? parsed
          : {}
      ),

      items,

      savedAt:
        now,

      updatedAt:
        now,

      meta: {
        ...(
          parsed?.meta ||
          {}
        ),

        etag:
          null,
      },
    };
  }

  window.localStorage.setItem(
    cacheKey,
    JSON.stringify(
      nextValue
    )
  );
}

export function readTruckSummaryCacheItems(
  cacheKey
) {
  if (
    !isBrowser() ||
    !cacheKey
  ) {
    return [];
  }

  try {
    const parsed =
      safeParse(
        window.localStorage.getItem(
          cacheKey
        )
      );

    return getSummaryCacheItems(
      parsed
    );
  } catch {
    return [];
  }
}

export function patchTruckSummaryCaches(
  truck
) {
  if (
    !isBrowser() ||
    !truck ||
    typeof truck !==
      "object" ||
    truck.id === null ||
    truck.id === undefined
  ) {
    return;
  }

  const truckId =
    String(
      truck.id
    );

  try {
    const matchingKeys =
      [];

    for (
      let index = 0;
      index <
      window.localStorage.length;
      index += 1
    ) {
      const key =
        window.localStorage.key(
          index
        );

      if (
        key &&
        key.endsWith(
          TRUCK_SUMMARY_CACHE_SUFFIX
        )
      ) {
        matchingKeys.push(
          key
        );
      }
    }

    matchingKeys.forEach(
      (
        cacheKey
      ) => {
        const parsed =
          safeParse(
            window.localStorage.getItem(
              cacheKey
            )
          );

        const currentItems =
          getSummaryCacheItems(
            parsed
          );

        const exists =
          currentItems.some(
            (
              item
            ) =>
              String(
                item?.id
              ) ===
              truckId
          );

        const nextItems =
          exists
            ? currentItems.map(
                (
                  item
                ) =>
                  String(
                    item?.id
                  ) ===
                  truckId
                    ? {
                        ...item,
                        ...truck,
                      }
                    : item
              )
            : [
                truck,
                ...currentItems,
              ];

        nextItems.sort(
          (
            first,
            second
          ) =>
            String(
              first?.plate ||
              ""
            ).localeCompare(
              String(
                second?.plate ||
                ""
              ),
              undefined,
              {
                sensitivity:
                  "base",
              }
            )
        );

        writeSummaryCacheItems(
          cacheKey,
          parsed,
          nextItems
        );
      }
    );
  } catch {
    // O estado em memória continua a ser atualizado pelo evento abaixo.
  }

  window.dispatchEvent(
    new CustomEvent(
      TRUCK_SUMMARY_CACHE_UPDATED_EVENT,
      {
        detail: {
          truck,
        },
      }
    )
  );
}

function isMeaningfulValue(
  value
) {
  if (
    value === true ||
    value === false
  ) {
    return true;
  }

  if (
    typeof value ===
    "number"
  ) {
    return true;
  }

  if (
    typeof value ===
    "string"
  ) {
    return (
      value.trim() !==
      ""
    );
  }

  if (
    Array.isArray(
      value
    )
  ) {
    return (
      value.length >
      0
    );
  }

  if (
    value &&
    typeof value ===
      "object"
  ) {
    return (
      Object.keys(
        value
      ).length >
      0
    );
  }

  return false;
}

/*
 * Junta dados parciais sem permitir que valores
 * vazios apaguem informação completa já guardada.
 */
export function mergeTruckDetailsData(
  current,
  incoming
) {
  const result = {
    ...(
      current ||
      {}
    ),
  };

  Object.entries(
    incoming ||
      {}
  ).forEach(
    ([
      key,
      value,
    ]) => {
      if (
        isMeaningfulValue(
          value
        )
      ) {
        result[key] =
          value;
      }
    }
  );

  return result;
}

/*
 * A leitura é síncrona para permitir que o EditTruck
 * nasça já preenchido no primeiro render.
 *
 * Mesmo que o timestamp esteja expirado, devolvemos
 * a informação guardada. O EditTruck atualiza-a
 * silenciosamente através do backend.
 */
export function readTruckDetailsCache(
  truckId
) {
  if (
    truckId === null ||
    truckId === undefined ||
    truckId === ""
  ) {
    return null;
  }

  const id =
    String(
      truckId
    );

  if (
    memoryCache.has(
      id
    )
  ) {
    return (
      memoryCache.get(
        id
      ) || null
    );
  }

  if (
    !isBrowser()
  ) {
    return null;
  }

  try {
    const stored =
      safeParse(
        window.localStorage.getItem(
          getTruckCacheKey(
            id
          )
        )
      );

    if (
      stored &&
      typeof stored ===
        "object"
    ) {
      memoryCache.set(
        id,
        stored
      );

      return stored;
    }
  } catch {
    return null;
  }

  return null;
}

export function writeTruckDetailsCache(
  truckId,
  truck
) {
  if (
    truckId === null ||
    truckId === undefined ||
    truckId === "" ||
    !truck ||
    typeof truck !==
      "object"
  ) {
    return;
  }

  const id =
    String(
      truckId
    );

  const data = {
    ...truck,
  };

  memoryCache.set(
    id,
    data
  );

  if (
    !isBrowser()
  ) {
    return;
  }

  try {
    /*
     * Mantemos os dados diretamente na chave porque
     * o Trucks.js existente já lê este formato.
     */
    window.localStorage.setItem(
      getTruckCacheKey(
        id
      ),
      JSON.stringify(
        data
      )
    );

    window.localStorage.setItem(
      getTruckTimestampKey(
        id
      ),
      String(
        Date.now()
      )
    );
  } catch {
    // Storage indisponível, cheio ou bloqueado.
  }
}

export function primeTruckDetailsCache(
truckId,
partialTruck
) {
if (
truckId === null ||
truckId === undefined ||
truckId === "" ||
!partialTruck
) {
return;
}

const id =
String(
truckId
);

const current =
readTruckDetailsCache(
truckId
);

const merged =
mergeTruckDetailsData(
current,
partialTruck
);

memoryCache.set(
id,
merged
);

if (
!isBrowser()
) {
return;
}

try {
window.localStorage.setItem(
getTruckCacheKey(
id
),
JSON.stringify(
merged
)
);
} catch {
// Storage indisponível, cheio ou bloqueado.
}
}

export function isTruckDetailsCacheFresh(
  truckId,
  maxAgeMs =
    TRUCK_DETAILS_TTL_MS
) {
  if (
    !isBrowser() ||
    truckId === null ||
    truckId === undefined ||
    truckId === ""
  ) {
    return false;
  }

  try {
    const savedAt =
      Number(
        window.localStorage.getItem(
          getTruckTimestampKey(
            String(
              truckId
            )
          )
        ) || 0
      );

    return (
      savedAt >
        0 &&
      Date.now() -
        savedAt <
        maxAgeMs
    );
  } catch {
    return false;
  }
}

export function removeTruckDetailsCache(
  truckId
) {
  if (
    truckId === null ||
    truckId === undefined ||
    truckId === ""
  ) {
    return;
  }

  const id =
    String(
      truckId
    );

  memoryCache.delete(
    id
  );

  if (
    !isBrowser()
  ) {
    return;
  }

  try {
    window.localStorage.removeItem(
      getTruckCacheKey(
        id
      )
    );

    window.localStorage.removeItem(
      getTruckTimestampKey(
        id
      )
    );
  } catch {
    // Storage indisponível.
  }
}